import React, { useState, useEffect } from 'react';
import { sendApiRequest } from '../../utils';

const KYCApprovalRequest = () => {
  const [kycRequests, setKycRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  const openDeleteModal = (request) => {
    setRequestToDelete(request);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setRequestToDelete(null);
  };

  const handleApproveReject = async (status) => {
    try {
      const response = await sendApiRequest(
        `api/kyc/update-approval`,
        { 
          kycId: selectedRequest._id,
          status,
          remarks: status === 'Rejected' ? 'Please check your documents' : 'All documents verified'
        },
        "PUT"
      );
      
      if (response.data.success) {
        setKycRequests(kycRequests.map(request => 
          request._id === selectedRequest._id ? { ...request, status } : request
        ));
        closeModal();
      }
    } catch (error) {
      console.error('Error updating KYC status:', error);
    }
  };

  const handleDeleteRequest = async () => {
    try {
      const response = await sendApiRequest(
        `api/kyc/delete/${requestToDelete._id}`,
        {},
        "DELETE"
      );
      
      if (response.data.success) {
        setKycRequests(kycRequests.filter(request => request._id !== requestToDelete._id));
        closeDeleteModal();
      }
    } catch (error) {
      console.error('Error deleting KYC request:', error);
    }
  };

  const fetchAllKYCRequests = async () => {
    try {
      const response = await sendApiRequest('api/kyc/all-history', {}, "GET");
      if (response.data.success) {
        setKycRequests(response.data.data);
        setFilteredRequests(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching KYC requests:', error);
    }
  };

  // Filter KYC requests based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredRequests(kycRequests);
    } else {
      const filtered = kycRequests.filter(request => 
        request.userDetails?.fullName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRequests(filtered);
    }
  }, [searchTerm, kycRequests]);

  useEffect(() => {
    fetchAllKYCRequests();
  }, []);

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">KYC Approval Requests</h2>
        <div className="relative w-64">
          <input
            type="text"
            placeholder="Search by name..."
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-3 px-4 text-left">Name</th>
              <th className="py-3 px-4 text-left">Email</th>
              <th className="py-3 px-4 text-left">Phone</th>
              <th className="py-3 px-4 text-left">PAN Number</th>
              <th className="py-3 px-4 text-left">Aadhaar Number</th>
              <th className="py-3 px-4 text-left">Status</th>
              <th className="py-3 px-4 text-left">Submitted On</th>
              <th className="py-3 px-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="8" className="py-4 text-center text-gray-500">
                  No KYC requests found{searchTerm ? ` matching "${searchTerm}"` : ''}
                </td>
              </tr>
            ) : (
              filteredRequests.map((request) => (
                <tr key={request._id}>
                  <td className="py-3 px-4">{request.userDetails?.fullName || 'User Deleted'}</td>
                  <td className="py-3 px-4">{request.userDetails?.email || 'N/A'}</td>
                  <td className="py-3 px-4">{request.userDetails?.phone || 'N/A'}</td>
                  <td className="py-3 px-4">{request.panNo}</td>
                  <td className="py-3 px-4">{request.aadhaarNo}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      request.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      request.status === 'Approved' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">{formatDate(request.createdAt)}</td>
                  <td className="py-3 px-4 flex space-x-2">
                    <button 
                      onClick={() => openModal(request)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => openDeleteModal(request)}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Details Modal */}
      {isModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  KYC Details - {selectedRequest.userDetails?.fullName || 'User Deleted'}
                </h3>
                <button 
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* User Info */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-lg border-b pb-2">User Information</h4>
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p>{selectedRequest.userDetails?.fullName || 'User Deleted'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p>{selectedRequest.userDetails?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p>{selectedRequest.userDetails?.phone || 'N/A'}</p>
                  </div>
                </div>

                {/* KYC Info */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-lg border-b pb-2">KYC Information</h4>
                  <div>
                    <p className="text-sm text-gray-500">PAN Number</p>
                    <p>{selectedRequest.panNo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Aadhaar Number</p>
                    <p>{selectedRequest.aadhaarNo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <p>{selectedRequest.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Submitted On</p>
                    <p>{formatDate(selectedRequest.createdAt)}</p>
                  </div>
                </div>

                {/* Document Links */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-lg border-b pb-2">Document Links</h4>
                  <div>
                    <p className="text-sm text-gray-500">PAN Scan Copy</p>
                    <a 
                      href={selectedRequest.panScanCopy} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View PAN Document
                    </a>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Aadhaar Scan Copy</p>
                    <a 
                      href={selectedRequest.aadhaarScanCopy} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View Aadhaar Document
                    </a>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cancel Cheque Copy</p>
                    <a 
                      href={selectedRequest.cancelChequeCopy} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View Cancel Cheque
                    </a>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {selectedRequest.status === 'Pending' && (
                <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">
                  <button
                    onClick={() => handleApproveReject('Rejected')}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApproveReject('Approved')}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Approve
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && requestToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Confirm Deletion</h3>
                <button 
                  onClick={closeDeleteModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="mb-6">
                Are you sure you want to delete the KYC request for {requestToDelete.userDetails?.fullName || 'this user'}?
                This action cannot be undone.
              </p>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteRequest}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCApprovalRequest;